import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Chart from 'react-apexcharts'
import styled from 'styled-components'
import dayjs from 'dayjs'
import BigNumber from 'bignumber.js'

import { precise, timeDurationMap } from 'monox/util'
import { weiToEth } from 'monox/constants'
import useWallet from 'hooks/useWallet'
import ChartPlaceholder from 'views/Swapper/components/ChartPlaceholder'

import { getPrices } from 'api'

const labelStyle = {
  fontFamily: 'Nunito',
  fontSize: '13px',
  fontWeight: 800,
}

const PriceChart = ({
  networkId,
  tokenAddress,
  decimals,
  timeRange,
  pid,
  translateId,
}) => {
  const vcashPrice =
    useSelector(({ application }) => application?.vcashPrice)?.[networkId] || 1
  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState([])
  const { chainId } = useWallet()
  const isDark = useSelector(({ application }) => application.isDark)
  const wallet = useSelector(({ user }) => user.wallet)

  const getData = useCallback(
    async (pid, timeRange) => {
      if (!chainId && wallet) return
      const now = dayjs()
      const selected = timeDurationMap?.[timeRange]
      const start = now.subtract(selected.interval, selected.unit)
      const params = `pid=eq.${pid}&timestamp=gte.${start.unix()}&timestamp=lte.${now.unix()}`

      const data = await getPrices(networkId || chainId, params)
      let records = []
      if (data?.result) {
        records = data?.response
          .sort((a, b) => a.timestamp - b.timestamp)
          .map((e) => {
            const timestamp = parseFloat(e?.timestamp)
            return {
              x: timestamp,
              y: weiToEth(new BigNumber(e?.price), 36 - decimals) * vcashPrice,
              '1W': dayjs.unix(timestamp).format(timeDurationMap['1W'].format),
              '24H': dayjs.unix(timestamp).format(timeDurationMap['24H'].format),
            }
          })
        setChartData(records)
      }
      setLoading(false)
    },
    [chainId]
  )

  useEffect(() => {
    if (tokenAddress) {
      setLoading(true)
      getData(pid, timeRange)
    }
  }, [tokenAddress, pid, timeRange, getData, chainId])

  if (loading) {
    return <LoadingContainerChart />
  }

  if (!loading && chartData.length < 2 && tokenAddress) {
    return (
      <ChartPlaceholder
        text="We are still collecting data for this market .."
        style={{ height: 200 }}
        translateId={translateId}
      />
    )
  }

  return (
    <Chart
      series={[
        {
          name: '',
          data: chartData,
        },
      ]}
      options={{
        colors: ['#3dcf97'],
        stroke: {
          width: 4,
          curve: 'smooth',
        },
        grid: {
          show: false,
        },
        chart: {
          id: 'coinChart',
          type: 'area',
          stacked: false,
          parentHeightOffset: 0,
          animations: {
            enabled: false,
          },
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            shadeIntensity: 1,
            gradientToColors: ['#f5f5f8'],
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0.48,
          },
        },
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
        },
        yaxis: {
          show: true,
          labels: {
            offsetX: -10,
            style: {
              ...labelStyle,
              cssClass: isDark ? 'custom-dark-axis-label' : 'custom-axis-label',
            },
            formatter: function (value) {
              return value ? '$' + precise(value, 4) : ''
            },
          },
        },
        xaxis: {
          show: true,
          tickAmount: 6,
          labels: {
            style: {
              ...labelStyle,
              cssClass: isDark ? 'custom-dark-axis-label' : 'custom-axis-label',
            },
            formatter: function (value) {
              return value
                ? dayjs.unix(value).format(timeDurationMap[timeRange].graphFormat)
                : ''
            },
            offsetX: 0,
          },
          title: { style: { ...labelStyle } },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        tooltip: {
          enabled: true,
          shared: false,
          marker: {
            show: false,
          },
          custom: function ({ dataPointIndex }) {
            return '$' + precise(chartData?.[dataPointIndex]?.y, 4)
          },
        },
      }}
      type={'area'}
      height="100%"
    />
  )
}

const LoadingContainerChart = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 22px;
  background-image: linear-gradient(
    to right,
    rgba(65, 222, 162, 0.45) 0%,
    rgba(65, 222, 162, 0.15) 100%
  );
  ${({ theme }) => theme.BlockLoading}
`
export default PriceChart
